import React, { useRef } from "react";
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ORG_LIST, EQP_LIST, NON_FIRE_EQP_LIST } from "../../../lib/mfBulkInput/constants";
import { fmtPhone } from "../../../lib/mfBulkInput/rowUtils";
import cls from "./MflistExcelinput.module.css";

function CellIcon({ status }) {
  if (status === "ok") return <span className={`${cls.cellIcon} ${cls.cellIconOk}`}>✓</span>;
  if (status === "error")
    return <span className={`${cls.cellIcon} ${cls.cellIconError}`}>!</span>;
  if (status === "warning")
    return <span className={`${cls.cellIcon} ${cls.cellIconWarning}`}>!</span>;
  return null;
}

function ValidatedCell({ row, fieldKey, required, children }) {
  const status = row.fieldStatus?.[fieldKey];
  const msg = row.fieldMessages?.[fieldKey];
  const cellClass = [
    cls.cellWrap,
    required ? cls.cellRequired : "",
    status === "error" ? cls.cellError : "",
    status === "warning" ? cls.cellWarning : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <TableCell
      className={cellClass}
      title={msg || undefined}
      sx={{ minWidth: fieldKey === "pnum" ? 100 : 64 }}
    >
      {children}
      <CellIcon status={status} />
    </TableCell>
  );
}

function rowResultChip(row) {
  if (row.saveStatus === "saved")
    return <Chip size="small" label="입력완료" color="success" />;
  if (row.saveStatus === "failed")
    return <Chip size="small" label="오류발생" color="error" />;
  if (row.saveStatus === "duplicate")
    return <Chip size="small" label="중복" color="error" />;
  if (row.rowStatus === "error")
    return <Chip size="small" label="오류" color="error" variant="outlined" />;
  if (row.rowStatus === "warning")
    return <Chip size="small" label="경고" color="warning" variant="outlined" />;
  if (row.rowStatus === "normal")
    return <Chip size="small" label="정상" color="success" variant="outlined" />;
  return <Chip size="small" label="입력전" variant="outlined" />;
}

export default function BulkInputGrid({
  rows,
  mode,
  updateField,
  deleteRow,
  onClearPhones,
  onRandomPhones,
  onQrPhones,
  rowRefs,
}) {
  return (
    <TableContainer
      className={mode === "training" ? cls.gridWrapTraining : ""}
      sx={{ height: "100%", border: "2px solid #7cb342" }}
    >
      <Table size="small" stickyHeader sx={{ borderCollapse: "collapse" }}>
        <TableHead>
          <TableRow>
            <TableCell colSpan={1} className={cls.groupHeader} />
            <TableCell colSpan={6} className={cls.groupHeader}>
              차량정보
            </TableCell>
            <TableCell colSpan={2} className={cls.groupHeader}>
              인원정보
            </TableCell>
            <TableCell colSpan={4} className={cls.groupHeader}>
              연락정보
            </TableCell>
            <TableCell colSpan={2} className={cls.groupHeader} />
          </TableRow>
          <TableRow>
            <TableCell className={cls.colHeader}>연번</TableCell>
            <TableCell className={cls.colHeader}>시도*</TableCell>
            <TableCell className={cls.colHeader}>소방서</TableCell>
            <TableCell className={cls.colHeader}>안전센터</TableCell>
            <TableCell className={cls.colHeader}>차량종류*</TableCell>
            <TableCell className={cls.colHeader}>차량명칭*</TableCell>
            <TableCell className={cls.colHeader}>차량번호</TableCell>
            <TableCell className={cls.colHeader}>탑승총인원</TableCell>
            <TableCell className={cls.colHeader}>선탑자명</TableCell>
            <TableCell className={cls.colHeader}>
              선탑전화번호*
              <button
                type="button"
                className={cls.btnAction}
                style={{ marginLeft: 4, padding: "0 4px", fontSize: "0.65rem" }}
                onClick={onRandomPhones}
              >
                랜덤
              </button>
              <button
                type="button"
                className={cls.btnAction}
                style={{ marginLeft: 2, padding: "0 4px", fontSize: "0.65rem" }}
                onClick={onQrPhones}
              >
                QR
              </button>
              <button
                type="button"
                className={cls.btnAction}
                style={{ marginLeft: 2, padding: "0 4px", fontSize: "0.65rem" }}
                onClick={onClearPhones}
              >
                초기화
              </button>
            </TableCell>
            <TableCell className={cls.colHeader}>자원집결지</TableCell>
            <TableCell className={cls.colHeader}>연락관</TableCell>
            <TableCell className={cls.colHeader}>비고</TableCell>
            <TableCell className={cls.colHeader}>입력결과</TableCell>
            <TableCell className={cls.colHeader}>삭제</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={15} align="center" sx={{ py: 4, color: "#888" }}>
                데이터가 없습니다. 엑셀을 불러오거나 훈련 데이터를 생성하세요.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                ref={(el) => {
                  if (rowRefs) rowRefs.current[row.id] = el;
                }}
                sx={{
                  backgroundColor: row.rowStatus === "error" ? "#ffebee" : undefined,
                }}
              >
                <TableCell align="center" sx={{ fontSize: "0.75rem", fontWeight: 700 }}>
                  {row.seq}
                </TableCell>
                <ValidatedCell row={row} fieldKey="sido" required>
                  <select
                    className={cls.cellSelect}
                    value={row.sido}
                    onChange={(e) => updateField(row.id, "sido", e.target.value)}
                  >
                    <option value="">-- 선택 --</option>
                    {ORG_LIST.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="fireStation">
                  <input
                    className={cls.cellInput}
                    value={row.fireStation}
                    onChange={(e) =>
                      updateField(row.id, "fireStation", e.target.value)
                    }
                  />
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="safetyCenter">
                  <input
                    className={cls.cellInput}
                    value={row.safetyCenter}
                    onChange={(e) =>
                      updateField(row.id, "safetyCenter", e.target.value)
                    }
                  />
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="eqp" required>
                  <select
                    className={cls.cellSelect}
                    value={row.eqp}
                    onChange={(e) => updateField(row.id, "eqp", e.target.value)}
                  >
                    <option value="">-- 선택 --</option>
                    <optgroup label="소방차량">
                      {EQP_LIST.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="비소방">
                      {NON_FIRE_EQP_LIST.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="callsgn" required>
                  <input
                    className={cls.cellInput}
                    value={row.callsgn}
                    onChange={(e) =>
                      updateField(row.id, "callsgn", e.target.value)
                    }
                  />
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="plateNo">
                  <input
                    className={cls.cellInput}
                    value={row.plateNo}
                    onChange={(e) =>
                      updateField(row.id, "plateNo", e.target.value)
                    }
                  />
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="sumnum">
                  <input
                    className={cls.cellInput}
                    type="number"
                    min="0"
                    value={row.sumnum}
                    onChange={(e) =>
                      updateField(row.id, "sumnum", e.target.value)
                    }
                  />
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="name">
                  <input
                    className={cls.cellInput}
                    value={row.name}
                    onChange={(e) => updateField(row.id, "name", e.target.value)}
                  />
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="pnum" required>
                  <input
                    className={cls.cellInput}
                    value={row.pnum}
                    placeholder="000-0000-0000"
                    onChange={(e) =>
                      updateField(row.id, "pnum", fmtPhone(e.target.value))
                    }
                  />
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="rdvs">
                  <input
                    className={cls.cellInput}
                    value={row.rdvs}
                    onChange={(e) => updateField(row.id, "rdvs", e.target.value)}
                  />
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="cntct">
                  <input
                    className={cls.cellInput}
                    value={row.cntct}
                    onChange={(e) => updateField(row.id, "cntct", e.target.value)}
                  />
                </ValidatedCell>
                <ValidatedCell row={row} fieldKey="remark">
                  <input
                    className={cls.cellInput}
                    value={row.remark}
                    onChange={(e) =>
                      updateField(row.id, "remark", e.target.value)
                    }
                  />
                </ValidatedCell>
                <TableCell align="center" sx={{ py: 0.5 }}>
                  {rowResultChip(row)}
                </TableCell>
                <TableCell align="center">
                  <button
                    type="button"
                    onClick={() => deleteRow(row.id)}
                    style={{
                      padding: "1px 6px",
                      fontSize: "0.72rem",
                      background: "#e53935",
                      color: "#fff",
                      border: "none",
                      borderRadius: 3,
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
